/**
 * Phase 41 Plan 41-11 (Surface 5a) — ProjectSlaTier admin API client.
 *
 * Wraps the Phase 38 SLA tier CRUD endpoints (verified mounted at
 * /api/projects/:projectId/sla-tiers via projectSlaTierRoutes.ts).
 *
 * Backend authorization is defense-in-depth (checker I-18): the SLA tier
 * routes use `requireAdminOrProjectMemberByProject` for GET and
 * `requireAdminOrProjectPM` for POST/PATCH/DELETE. The admin page that
 * consumes this client is additionally gated by `<AdminRoute>` (plan
 * 41-03 route tree).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

// Petra UAT 2026-05-20 (test D) — backend Phase 38 contract uses
// responseTargetMinutes / resolutionTargetMinutes (minutes, not seconds) and
// two separate threshold arrays (responseWarningThresholds +
// resolutionWarningThresholds). The previous Phase 41 admin client typed
// these as responseTargetSec + a single warningThresholdPcts which crashed
// SlaTiersAdminPage on render once any project actually had tiers (the
// `.map()` access on the missing field threw before TopBar mounted).
// Shape mirrors backend/src/api/schemas/projectSlaTier.ts verbatim.
export interface ProjectSlaTier {
  id: number;
  projectId: number;
  name: string;
  orderIndex: number;
  responseTargetMinutes: number;
  resolutionTargetMinutes: number;
  responseWarningThresholds: number[];
  resolutionWarningThresholds: number[];
}

export interface CreateProjectSlaTierInput {
  name: string;
  orderIndex: number;
  responseTargetMinutes: number;
  resolutionTargetMinutes: number;
  responseWarningThresholds: number[];
  resolutionWarningThresholds: number[];
}
export type UpdateProjectSlaTierInput = Partial<CreateProjectSlaTierInput>;

export function fetchProjectSlaTiers(projectId: number) {
  return apiGet<{ data: ProjectSlaTier[] }>(`/projects/${projectId}/sla-tiers`);
}
export function createProjectSlaTier(projectId: number, input: CreateProjectSlaTierInput) {
  return apiPost<{ data: ProjectSlaTier }>(`/projects/${projectId}/sla-tiers`, input);
}
export function updateProjectSlaTier(
  projectId: number,
  id: number,
  input: UpdateProjectSlaTierInput,
) {
  return apiPatch<{ data: ProjectSlaTier }>(`/projects/${projectId}/sla-tiers/${id}`, input);
}
export function deleteProjectSlaTier(projectId: number, id: number) {
  return apiDelete<void>(`/projects/${projectId}/sla-tiers/${id}`);
}
